import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Pagination, { PaginationPresentationProps } from "@waytek/MobiusOverrides/Pagination/Pagination";
import setFilterQuery from "@waytek/Store/Components/Blog/SetFilterQuery";
import { initialBlogState } from "@waytek/Store/Custom/Blog/BlogListState";
import { CustomState } from "@waytek/Store/Reducers";
import { fontFamily, mediaQueriesBP } from "@waytek/Utilities/variables";
import BlogCardComponent, {
    blogCardComponentStyles,
    BlogCardComponentStyles,
} from "@waytek/Widgets/Custom/BlogCardComponent";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

const mapStateToProps = (customState: CustomState) => {
    const pagination = customState.blogList.pagination;
    return {
        isLoading: customState.blogList.isLoading,
        posts: customState.blogList.posts || [],
        pagination,
    };
};

const mapDispatchToProps = {
    setFilterQuery,
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface BlogListStyles {
    wrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noPostsText?: TypographyPresentationProps;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    gridContainerWrapper?: InjectableCss;
    gridContainerPosts?: GridContainerProps;
    gridItemPost?: GridItemProps;
    blogCardExtendedStyle?: BlogCardComponentStyles;
    pagination?: PaginationPresentationProps;
    bottomPaginationContainer?: InjectableCss;
    topPaginationContainer?: InjectableCss;
}

const customBlogCardComponentStyles: BlogCardComponentStyles = {
    ...blogCardComponentStyles,
    textWrapper: {
        ...blogCardComponentStyles.textWrapper,
        css: css`
            ${blogCardComponentStyles.textWrapper?.css}
            padding: 22px 25px 24px;
        `,
    },
    date: {
        ...blogCardComponentStyles.date,
        css: css`
            ${blogCardComponentStyles.date?.css}
            margin: 0;
        `,
    },
};

export const blogListStyles: BlogListStyles = {
    centeringWrapper: {
        css: css`
            width: 100%;
            height: 300px;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    titleWrapper: {
        css: css`
            margin-bottom: 26px;
            display: flex;
            justify-content: space-between;
            align-items: center;

            ${mediaQueriesBP.maxMD} {
                flex-wrap: wrap;
            }
        `,
    },
    pagination: {
        cssOverrides: {
            linkList: css`
                ${mediaQueriesBP.maxSM} {
                    margin-top: 12px;
                    width: 100vw;

                    ul {
                        li {
                            &:nth-child(-n + 2),
                            &:nth-last-child(-n + 2) {
                                button {
                                    padding: 0 5px;
                                }
                            }
                        }
                    }
                }

                ${mediaQueriesBP.maxMD} {
                    margin-top: 12px;
                }
            `,
            perPageSelect: css`
                ${mediaQueriesBP.maxLG} {
                    width: auto;
                }

                ${mediaQueriesBP.maxMD} {
                    width: 100%;
                }
            `,
        },
    },
    title: {
        weight: 500,
        lineHeight: "normal",
        fontFamily,
        css: css`
            font-size: 26px;

            ${mediaQueriesBP.minMD} {
                font-size: 36px;
            }
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noPostsText: {
        size: 24,
        weight: "bold",
        css: css`
            text-align: center;
        `,
    },
    gridContainerWrapper: {
        css: css`
            ${mediaQueriesBP.maxMD} {
                padding: 0 8px;
            }
        `,
    },
    gridContainerPosts: {
        gap: 20,
    },
    gridItemPost: {
        width: [12, 6, 4, 3, 3],
    },
    bottomPaginationContainer: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 30px;

            ${mediaQueriesBP.maxMD} {
                justify-content: center;
                margin-top: 40px;
            }

            & > div {
                justify-content: flex-end;
                flex-wrap: wrap;
                ${mediaQueriesBP.maxMD} {
                    justify-content: center;

                    & > div {
                        flex-wrap: wrap;
                    }
                }

                ${mediaQueriesBP.minMD} {
                    & > div {
                        flex-wrap: nowrap;
                    }
                }
            }
        `,
    },
    topPaginationContainer: {
        css: css`
            margin-right: -15px;

            ${mediaQueriesBP.maxMD} {
                margin-right: 0;
                width: 100%;
            }

            ${mediaQueriesBP.maxSM} {
                width: 100vw;
                margin-right: 0;
                margin-left: -20px;
            }
        `,
    },
    blogCardExtendedStyle: customBlogCardComponentStyles,
};

const styles = blogListStyles;

const BlogList: FC<Props> = ({ isLoading, posts, fields, pagination, setFilterQuery }) => {
    if (isLoading && !posts.length) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (posts.length === 0) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <Typography {...styles.noPostsText}>{translate("We're sorry, there were no posts found.")}</Typography>
            </StyledWrapper>
        );
    }

    if (!pagination || !pagination.totalItemCount) {
        return null;
    }

    const changePage = (page: number) => {
        setFilterQuery({
            parameters: {
                page,
            },
        });
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const pageSize = parseInt(event.currentTarget.value, 10);
        setFilterQuery({
            parameters: {
                page: initialBlogState.parameters.page,
                pageSize,
            },
        });
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <LoadingOverlay loading={isLoading}>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography as="h2" variant="body" {...styles.title}>
                        {fields.title}
                    </Typography>

                    <StyledWrapper {...styles.topPaginationContainer}>
                        <Pagination
                            {...styles.pagination}
                            resultsCount={pagination.totalItemCount}
                            resultsPerPage={pagination.pageSize}
                            resultsPerPageOptions={pagination.pageSizeOptions}
                            currentPage={pagination.page}
                            onChangePage={changePage}
                            onChangeResultsPerPage={changeResultsPerPage}
                            showResultsPerPage={false}
                        />
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper {...styles.gridContainerWrapper}>
                    <GridContainer {...styles.gridContainerPosts}>
                        {posts.map(post => {
                            const blogLink: any = {
                                id: post.id,
                                parentId: post.parentId,
                                url: post.url,
                            };

                            return (
                                <GridItem {...styles.gridItemPost} key={post.id}>
                                    <BlogCardComponent
                                        blogLink={blogLink}
                                        openNewWindow={false}
                                        extendedStyles={styles.blogCardExtendedStyle}
                                    />
                                </GridItem>
                            );
                        })}
                    </GridContainer>
                    <GridContainer {...styles.bottomPaginationContainer}>
                        <Pagination
                            {...styles.pagination}
                            resultsCount={pagination.totalItemCount}
                            resultsPerPage={pagination.pageSize}
                            resultsPerPageOptions={pagination.pageSizeOptions}
                            currentPage={pagination.page}
                            onChangePage={changePage}
                            onChangeResultsPerPage={changeResultsPerPage}
                            showResultsPerPage={false}
                        />
                    </GridContainer>
                </StyledWrapper>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BlogList),
    definition: {
        group: "Blog" as any,
        displayName: "Blog List",
        allowedContexts: ["BlogLandingPage" as any],
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "Recent Posts",
                fieldType: "Translatable",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
